import shortid from "shortid";
import { observable, action, runInAction, computed } from "mobx";
import firebase from "firebase/app";
import "firebase/storage";

import RootStore from "app/RootStore";
import CommunityService from "contexts/community/CommunityService";
import { resizeSquareImage } from "lib/image";

// Using dedicated storage bucket to store community-profile images.
const FIREBASE_STORAGE_BUCKET = "intranet";
const FIREBASE_CONFIG_MOBILE = {
  apiKey: "AIzaSyAsN3fhBebx9WNFr-P1GIeVtAJw7PIAeJM",
  authDomain: "tracker-31d5e.firebaseapp.com",
  databaseURL: "https://tracker-31d5e.firebaseio.com",
  projectId: "tracker-31d5e",
  storageBucket: "tracker-31d5e.appspot.com",
  messagingSenderId: "759019709617",
  appId: "1:759019709617:web:6ffb00b858f7452b632e90",
  measurementId: "G-JQF5W04L2P",
};
firebase.initializeApp(FIREBASE_CONFIG_MOBILE);
const storage = firebase.storage();

export default class CommunityStore {
  private rootStore: RootStore;
  private communityService: CommunityService;

  @observable dataMapDummyAccount: Record<string, ResCommunityProfile> = {};
  @observable dataListPost: ResPost[] = [];
  @observable pageNumber = 1;
  @observable postCount = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.communityService = new CommunityService();
  }

  @computed
  get dataListDummyAccount() {
    return Object.values(this.dataMapDummyAccount);
  }

  /**
   * is the client dummy?
   */
  isDummyClient(userId: number) {
    return !!this.dataMapDummyAccount[userId];
  }

  /**
   * Loads comments.
   */
  async loadListComment(postId: number, page: number, size: number) {
    const res = await this.communityService.getListComment(postId, page, size);
    return res;
  }

  /**
   * Loads a post.
   */
  async loadPost(postId: number) {
    this.loadListDummyClient();
    const res = await this.communityService.getPost(postId);
    return res;
  }

  /**
   * Reloads posts.
   */
  async reloadListPost() {
    await this.loadListPost(this.pageNumber);
  }

  /**
   * Loads posts.
   */
  @action
  async loadListPost(page: number) {
    const res = await this.communityService.getListPost({
      page,
    });
    runInAction(() => {
      this.dataListPost = res.items;
      this.pageNumber = res.page_number;
      this.postCount = res.total_count;
    });
    return res.items;
  }

  /**
   * Loads dummy clients.
   */
  @action
  async loadListDummyClient() {
    const res = await this.communityService.getListDummyClient();
    runInAction(() => {
      this.dataMapDummyAccount = res.reduce(
        (acc, x) => ({ ...acc, [x.id]: x }),
        {},
      );
    });
  }

  /**
   * Writes a post as a new user.
   */
  async writePostAsNewUser(data: {
    firstName: string;
    lastName: string;
    title: string;
    contents: string;
    image?: File;
  }) {
    const payload: ReqPostNewUser = {
      firstName: data.firstName,
      lastName: data.lastName,
      title: data.title,
      contents: data.contents,
    };
    if (data.image) {
      // Upload image to Firebase storage and retrieve the URL.
      const imageResized = await resizeSquareImage(data.image);
      payload.profileImage = await this.uploadImageToFirebaseStorage(
        imageResized,
      );
    }
    await this.communityService.writePostAsNewUser(payload);
  }

  /**
   * Write a post as an existing user.
   */
  async writePostAsExistingUser(data: {
    userId: number;
    title: string;
    contents: string;
  }) {
    await this.communityService.writePostAsExistingUser(data);
  }

  /**
   * Uploads given image file to Firebase storage, and returns download URL.
   */
  async uploadImageToFirebaseStorage(image: File): Promise<string> {
    const fileName = `${shortid.generate()}_${image.name}`;
    return new Promise((resolve, reject) => {
      const upload = storage
        .ref(`/${FIREBASE_STORAGE_BUCKET}/${fileName}`)
        .put(image);
      upload.on(
        "state_changed",
        console.log,
        (err) => {
          reject(err);
        },
        () => {
          storage
            .ref(FIREBASE_STORAGE_BUCKET)
            .child(fileName)
            .getDownloadURL()
            .then((firebaseURL) => {
              resolve(firebaseURL);
            })
            .catch((err) => {
              console.log(err);
              resolve("");
            });
        },
      );
    });
  }

  /**
   * Write a comment as a new user.
   */
  async writeCommentAsNewUser(data: {
    postId: number;
    firstName: string;
    lastName: string;
    comment: string;
    image?: File;
  }) {
    const reqProfile: ReqCommunityProfile = {
      firstName: data.firstName,
      lastName: data.lastName,
    };
    if (data.image) {
      const imageResized = await resizeSquareImage(data.image);
      reqProfile.profileImage = await this.uploadImageToFirebaseStorage(
        imageResized,
      );
    }
    const { token } = await this.communityService.createDummyClient(reqProfile);
    await this.communityService.createComment(token, data.postId, data.comment);
  }

  /**
   * Write a comment as an existing user.
   */
  async writeCommentAsExistingUser(data: {
    userId: number;
    postId: number;
    comment: string;
  }) {
    const email = this.dataMapDummyAccount[data.userId].email;
    const { token } = await this.communityService.signInClient(email);
    await this.communityService.createComment(token, data.postId, data.comment);
  }
}
